import React, { useMemo, useCallback } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import CartReviewPriceStack from './CartReviewPriceStack'
import CartReviewBanner from './CartReviewBanner'
import { getBannerTypeVariant } from './CartReviewUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import useUserCartExperienceItemData from 'hooks/UserCart/useUserCartExperienceItemData'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import CartReviewTileImageContent from './CartReviewTileImageContent'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/UserCartActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import CondensedCartReviewTile from './CondensedCartReviewTile'
import CondensedCartReviewTileContent from './CondensedCartReviewTileContent'
import CartReviewTileContent from './CartReviewTileContent'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import CartReviewTileActions from './CartReviewTileActions'

interface Props {
  item: App.Checkout.ExperienceItem
  tileVariant?: 'default' | 'condensed'
}

function ExperienceCartReviewTile({ item, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const data = useUserCartExperienceItemData(item)

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater([item.itemId]))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const formattedDateRange = useMemo(() => {
    if (!item.date) return ''
    const formattedDate = moment(item.date).format(DMY_CASUAL_FORMAT)
    if (!data.experience?.hasTimeslots) return formattedDate
    return item.time ? `${formattedDate} · ${item.time}` : formattedDate
  }, [item.date, item.time, data.experience?.hasTimeslots])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)
  const cancellationInfo = data.experience?.copy.cancellationInfo

  const formattedTickets = useMemo(() => {
    return item.tickets
      .filter(ticket => ticket.count > 0)
      .map(ticket => `${ticket.count} × ${ticket.name}`)
  }, [item.tickets])

  const experienceURL = useMemo(() =>
    getExperienceOfferPageURL(data.experience),
  [data.experience],
  )

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    return {
      calculationRequests: [
        {
          price: publicTotal,
          loyaltyProductType: 'experience',
        },
      ],
      calculationType: 'exact',
    }
  }, [publicTotal])

  const image = data.experience?.images[0]

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (data.isDeleted) {
    return <DeletedCartReviewTile
      title={data.experience?.name || ''}
      itemIds={item.itemId}
    />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        to={experienceURL}
        banner={<CartReviewBanner
          variant={bannerTypeVariant}
        />}
        onDelete={handleDelete}
        content={<CondensedCartReviewTileContent
          title={data.experience?.name || ''}
          dates={formattedDateRange}
          packageTitle={formattedTickets}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        price={<CartReviewPriceStack
          price={publicTotal}
          memberPrice={memberTotal}
          value={data.itemTotals?.value ?? 0}
          luxPlusOfferConfig={data.experience?.luxPlus}
        />}
      />
    )
  }

  return <CartReviewTile
    to={experienceURL}
    imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
    content={<CartReviewTileContent
      title={data.experience?.name || ''}
      dates={formattedDateRange}
      packageTitle={formattedTickets}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      cancellationPolicy={cancellationInfo && (
        <ExperienceCancellationText
          cancellationInfo={cancellationInfo}
          date={item.date}
          textSize="medium"
        />
      )}
    />}
    price={<CartReviewPriceStack
      price={publicTotal}
      memberPrice={memberTotal}
      value={data.itemTotals?.value ?? 0}
      luxPlusOfferConfig={data.experience?.luxPlus}
    />}
    banner={<CartReviewBanner
      variant={bannerTypeVariant}
      to={experienceURL}
    />}
    isBookable={data.isBookable}
    saveForLater={data.isSavedForLater}
    actions={<CartReviewTileActions
      isBookable={data.isBookable}
      saveForLater={data.isSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />}
  />
}

export default ExperienceCartReviewTile
